import React, { useRef, useState } from "react";
import { Box, Container, Dialog, Paper, Typography } from "@mui/material";
import { useDispatch, useSelector } from "../../../store";
import DeleteConfirmation from "../../base/DeleteConfirmation";
import {
  NotificationsActive,
  Campaign,
  TrackChanges,
} from "@mui/icons-material";
import InfoBox from "../../base/InfoBox";
import InfoBoxGroup from "../../base/InfoBoxGroup";
import ScheduledReportsTable from "./ScheduledReportsTable";
import Button from "../../base/Button";
import NotificationSchedule from "../../../types/notificationSchedule";
import AddEditSchedule, { ScheduleFormSubmitHandler } from "./AddEditSchedule";
import Modal from "../../base/Modal";
import {
  clearSelectedSchedule,
  getSchedule,
  deleteSchedule,
  fetchActiveSchedules,
  disableSchedule,
  addSchedule,
  editSchedule,
} from "../../../slices/schedule";
import ScheduledReportsStack from "./ScheduledReportsStack";

interface Props {}

const ScheduledReportsView: React.FC<Props> = () => {
  const dispatch = useDispatch();

  const { status, activeReports } = useSelector((store) => store.schedules);

  const formSubmitHandlerRef = useRef<ScheduleFormSubmitHandler | null>(null);

  const [modalOpen, setModalOpen] = useState<
    "add" | "edit" | "delete" | "disable" | "none"
  >("none");
  const [selectedReport, setSelectedReport] = useState<number | undefined>();
  const [selectedSchedule, setSelectedSchedule] = useState<
    number | undefined
  >();

  const handleCloseModal = () => {
    setModalOpen("none");
    dispatch(clearSelectedSchedule());
    dispatch(fetchActiveSchedules());
    setSelectedReport(undefined);
    setSelectedSchedule(undefined);
  };

  const handleOpenAddNewSchedule = () => {
    dispatch(clearSelectedSchedule());
    setModalOpen("add");
  };

  const handleOpenReportDisable = (id: number) => {
    setSelectedReport(id);
    setModalOpen("disable");
  };

  const handleOpenGroupDelete = (id: number) => {
    setSelectedSchedule(id);
    setModalOpen("delete");
  };

  const handleOpenGroupEdit = (id: number) => {
    dispatch(getSchedule(id));
    setModalOpen("edit");
  };

  const handleViewReport = (id: number) => {
    window.open(`tile/${id}`, "_blank");
  };

  const handleEditReport = (id: number) => {
    window.open(`edit-tile/${id}`, "_blank");
  };

  const handleSaveScheduleGroup = async (data: NotificationSchedule) => {
    if (modalOpen === "add") {
      await dispatch(addSchedule(data));
    } else if (modalOpen === "edit") {
      await dispatch(editSchedule(data));
    }

    handleCloseModal();
  };

  const handleDisableReport = async () => {
    await dispatch(disableSchedule(selectedReport!));
    handleCloseModal();
  };

  const handleDeleteScheduleGroup = async () => {
    await dispatch(deleteSchedule(selectedSchedule!));
    handleCloseModal();
  };

  const infoBoxes = [
    {
      text: "The table below displays available schedules for sending reports.",
      icon: <NotificationsActive fontSize="inherit" color="warning" />,
    },
    {
      text: "Click the buttons in the Action column to modify or disable reports for a tile.",
      icon: <TrackChanges fontSize="inherit" color="inherit" />,
    },
    {
      text: "Visit the Notification Setup page to configure notification groups and how reports are sent.",
      icon: <Campaign fontSize="inherit" color="success" />,
    },
  ];

  return (
    <Container sx={{ marginBottom: 8 }}>
      <InfoBoxGroup
        content={infoBoxes.map((info, index) => {
          return <InfoBox key={index} text={info.text} icon={info.icon} />;
        })}
      />
      <Paper
        sx={{
          paddingY: 3,
          paddingX: { xs: 2, md: 5 },
          marginBottom: 5,
        }}
      >
        <Box
          display="flex"
          flexDirection={{ md: "row", xs: "column" }}
          justifyContent={{ xs: "center", md: "space-between" }}
          alignItems="center"
          sx={{
            paddingY: 1,
            paddingX: 2,
          }}
          gap={1}
        >
          <Typography fontWeight="bold">Report Schedules</Typography>
          <Typography
            display={{ md: "flex", xs: "none" }}
            variant="caption"
            sx={{
              visibility: activeReports?.length! > 0 ? "visible" : "hidden",
            }}
          >
            Click each row to view tiles with scheduled reports
          </Typography>
          <Button
            label="Add New"
            variant="contained"
            color="info"
            onClick={handleOpenAddNewSchedule}
          />
        </Box>
        <Box
          display="flex"
          flexDirection={"row"}
          justifyContent={"center"}
          alignItems="center"
          sx={{
            paddingY: 1,
            paddingX: 2,
          }}
        >
          <Typography textAlign="center" variant="caption">
            Schedules will combine all added tiles into a single email report.
            To separate reports, create multiple schedules and add different
            tiles to each schedule.
          </Typography>
        </Box>
        <Box
          display={{ xs: "none", md: "flex" }}
          flexDirection="row"
          justifyContent="center"
          marginY={2}
          sx={{ maxWidth: "100%" }}
        >
          <ScheduledReportsTable
            handleViewReport={handleViewReport!}
            handleEditReport={handleEditReport!}
            handleDisableReport={handleOpenReportDisable!}
            handleEditGroup={handleOpenGroupEdit!}
            handleDeleteGroup={handleOpenGroupDelete!}
          />
        </Box>
        <Box
          display={{ xs: "flex", md: "none" }}
          flexDirection="row"
          justifyContent="center"
          marginY={2}
          sx={{ width: "100%" }}
        >
          <ScheduledReportsStack
            handleViewReport={handleViewReport!}
            handleEditReport={handleEditReport!}
            handleDisableReport={handleOpenReportDisable!}
            handleEditGroup={handleOpenGroupEdit!}
            handleDeleteGroup={handleOpenGroupDelete!}
          />
        </Box>
      </Paper>
      <Dialog open={modalOpen === "disable"}>
        <DeleteConfirmation
          name="this tile from the schedule"
          extraInfo="This will stop all future reports on this tile. Return to tile edit page to add a new report."
          handleClose={handleCloseModal}
          handleDelete={handleDisableReport!}
          loading={status === "loading"}
        />
      </Dialog>
      <Dialog open={modalOpen === "delete"}>
        <DeleteConfirmation
          name="this report schedule"
          extraInfo="This will remove the schedule from all tiles. Click 'Add New' to create a new schedule."
          handleClose={handleCloseModal}
          handleDelete={handleDeleteScheduleGroup!}
          loading={status === "loading"}
        />
      </Dialog>
      <Modal
        open={modalOpen === "add" || modalOpen === "edit"}
        onClose={handleCloseModal}
        onSave={() => formSubmitHandlerRef.current?.()}
        title={`${modalOpen === "add" ? "Add New" : "Edit"} Schedule`}
        loading={status === "loading"}
      >
        <AddEditSchedule
          type={modalOpen === "add" ? "add" : "edit"}
          onSubmit={handleSaveScheduleGroup!}
          ref={formSubmitHandlerRef}
        />
      </Modal>
    </Container>
  );
};

export default ScheduledReportsView;
